import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/GlassCard";
import { UserPlus, KeyRound, ShieldOff, Pencil, Check, X, Shield, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetShow, setResetShow] = useState(null); // { type, password?, name }

  const refresh = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users || []);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const resetPassword = async (u) => {
    if (!confirm(`Reset password for ${u.email}? A new random password will be generated.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetShow({ type: "password", password: data.new_password, name: u.email });
    } catch (e) {
      setMsg({ kind: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const reset2fa = async (u) => {
    if (!confirm(`Reset 2FA for ${u.email}? They will need to re-enroll their authenticator on next login.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/reset-2fa`);
      setMsg({ kind: "ok", text: `2FA reset for ${u.email}. They must re-enroll on next login.` });
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="text-center py-20 text-stone-500" data-testid="admin-no-access">
        You need administrator privileges to view this page.
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-stone-500 flex items-center gap-2">
            <Shield size={14} /> Administration
          </p>
          <h1 className="font-serif text-5xl">Users</h1>
        </div>
        <button
          className="btn-gold flex items-center gap-2"
          onClick={() => setCreating(true)}
          data-testid="admin-new-user-btn"
        >
          <UserPlus size={16} /> New user
        </button>
      </div>

      {msg && (
        <div
          className={`rounded-2xl px-4 py-3 text-sm ${
            msg.kind === "ok"
              ? "bg-emerald-50/70 text-emerald-700"
              : "bg-red-50/70 text-red-700"
          }`}
        >
          {msg.text}
        </div>
      )}

      <GlassCard className="p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-6 py-3 text-[11px] uppercase tracking-wider text-stone-500 bg-white/40 border-b border-stone-200/60">
          <div className="col-span-3">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">2FA</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-stone-200/50">
          {users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-12 gap-3 px-6 py-4 items-center hover:bg-white/40 transition-colors"
              data-testid={`user-row-${u.id}`}
            >
              <div className="col-span-3 font-medium truncate">{u.name}</div>
              <div className="col-span-4 text-sm text-stone-600 truncate">{u.email}</div>
              <div className="col-span-2">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    u.role === "admin"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-stone-100 text-stone-600"
                  }`}
                >
                  {u.role}
                </span>
              </div>
              <div className="col-span-1">
                {u.totp_setup_complete ? (
                  <Check size={14} className="text-emerald-600" />
                ) : (
                  <X size={14} className="text-stone-400" />
                )}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button
                  onClick={() => setEditing(u)}
                  className="w-8 h-8 rounded-full hover:bg-white inline-flex items-center justify-center"
                  data-testid={`edit-user-${u.id}`}
                  title="Edit"
                >
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => resetPassword(u)}
                  disabled={busy}
                  className="w-8 h-8 rounded-full hover:bg-white inline-flex items-center justify-center"
                  data-testid={`reset-pw-${u.id}`}
                  title="Reset password"
                >
                  <KeyRound size={14} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => reset2fa(u)}
                  disabled={busy}
                  className="w-8 h-8 rounded-full hover:bg-white inline-flex items-center justify-center text-rose-600"
                  data-testid={`reset-2fa-${u.id}`}
                  title="Reset 2FA"
                >
                  <ShieldOff size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      {creating && (
        <CreateUserDialog open={creating} onClose={() => setCreating(false)} onSaved={refresh} />
      )}
      {editing && (
        <EditUserDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
          user={editing}
        />
      )}
      {resetShow && (
        <ResetResultDialog result={resetShow} onClose={() => setResetShow(null)} />
      )}
    </div>
  );
}

function CreateUserDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-strong rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">New user</DialogTitle>
          <DialogDescription>
            Set an initial password. The user will set up their 2FA on first login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name">
            <input
              required
              autoFocus
              type="text"
              data-testid="new-user-name"
              className="input-glass"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <input
              required
              type="email"
              data-testid="new-user-email"
              className="input-glass"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Initial password (min 6)">
            <input
              required
              minLength={6}
              type="text"
              data-testid="new-user-password"
              className="input-glass font-mono"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="input-glass" data-testid="new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-strong rounded-xl">
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {err && (
            <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2">
              {err}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-gold flex-1"
              data-testid="new-user-save"
            >
              {busy ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ open, onClose, onSaved, user }) {
  const [form, setForm] = useState({ name: user.name, email: user.email, role: user.role });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, form);
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-strong rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit user</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name">
            <input
              required
              type="text"
              data-testid="edit-user-name"
              className="input-glass"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <input
              required
              type="email"
              data-testid="edit-user-email"
              className="input-glass"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="input-glass" data-testid="edit-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-strong rounded-xl">
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {err && (
            <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2">
              {err}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-gold flex-1"
              data-testid="edit-user-save"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetResultDialog({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="glass-strong rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Password reset</DialogTitle>
          <DialogDescription>
            Share this password securely with <b>{result.name}</b>. It is shown only once.
          </DialogDescription>
        </DialogHeader>
        <div className="glass rounded-2xl p-4 text-center">
          <div className="label-tiny">New temporary password</div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(result.password);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="mt-2 font-mono text-lg bg-white rounded-xl px-4 py-2 inline-flex items-center gap-2 hover:shadow"
            data-testid="reset-password-value"
          >
            {result.password}
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
        <button onClick={onClose} className="btn-gold w-full mt-4">
          Done
        </button>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tiny">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
